import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { UserState } from "@/context/User/UserReducer";
import { updateInsurer } from "@/services/Insurer.service";
import { Insurer, UpdateInsurerParams } from "@/types/insurer.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../ui/form";

const formSchema = z.object({
	insurerid: z.number(),
	insurername: z.string(),
});

const EditInsurer: React.FC<{
	user: UserState | null;
	insurer: Insurer;
}> = ({ user, insurer }) => {
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const editInsurer = useMutation({
		mutationKey: ["insurer_update", insurer.insurerid],
		mutationFn: (data: UpdateInsurerParams) =>
			updateInsurer(user?.accessToken!, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["insurer", insurer.insurerid.toString()],
				exact: true,
				refetchType: "all",
			});
			setOpen(false);
			form.reset();
			toast.success("Success", {
				description: `insurer ${variables.insurername} has been successfully created`,
			});
			editInsurer.reset();
		},
		onError: (err) => {
			toast.error("Error!", { description: "Error adding insurer, try again" });
			console.log(err);
		},
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			insurerid: insurer.insurerid,
			insurername: insurer.insurername,
		},
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		editInsurer.mutate(data);
	};

	const disabled = () => {
		if (editInsurer.isPending) return true;
		if (editInsurer.isSuccess) return true;
		return false;
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button variant="default">Edit Insurer</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-left font-bold text-2xl">
						Edit Insurer
					</DialogTitle>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<FormField
							name="insurerid"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Insurer ID</FormLabel>
									<FormControl>
										<Input
											type="number"
											defaultValue={insurer.insurerid}
											onChange={(e) => field.onChange(e.target.valueAsNumber)}
											disabled
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="insurername"
							render={({ field }) => (
								<FormItem className="flex-1 mb-2">
									<FormLabel>Name of insurer</FormLabel>
									<FormControl>
										<Input type="text" {...field} disabled={disabled()} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							className="mt-4 w-full"
							disabled={editInsurer.isPending || editInsurer.isSuccess}
						>
							{editInsurer.isPending ? "Updating..." : "Edit Insurer"}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default EditInsurer;
